import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  message,
  Descriptions,
  Avatar,
  Input,
  Popconfirm
} from 'antd';
import {
  UserOutlined,
  EyeOutlined,
  ReloadOutlined,
  WechatOutlined,
  DeleteOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Search } = Input;

const ParentList = () => {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [parents, setParents] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [boundStudents, setBoundStudents] = useState([]);

  // 获取家长列表
  const fetchParents = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page,
        pageSize,
        user_type: 'parent',
        search
      });
      
      const response = await fetch(`/api/v2/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setParents(data.data.users || []);
        setPagination({
          current: page,
          pageSize,
          total: data.data.total || 0
        });
      } else {
        message.error(data.message || '获取家长列表失败');
      }
    } catch (error) {
      console.error('获取家长列表失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParents();
  }, []);

  useEffect(() => {
    fetchParents(1, pagination.pageSize, searchKeyword);
  }, [searchKeyword]);

  // 获取家长绑定的学生
  const fetchBoundStudents = async (parentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/users/${parentId}/bound-students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setBoundStudents(data.data || []);
      }
    } catch (error) {
      console.error('获取绑定学生失败:', error);
    }
  };

  // 删除家长
  const handleDeleteParent = async (parentId, name) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/users/${parentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        message.success(`家长 ${name} 已删除`);
        fetchParents(pagination.current, pagination.pageSize, searchKeyword);
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      message.error('网络错误');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '家长信息',
      key: 'parent',
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar 
            src={record.wechat_avatar} 
            icon={<UserOutlined />}
            size="default"
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name || record.wechat_nickname || '未设置'}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              @{record.username}
            </div>
          </div>
        </Space>
      )
    },
    {
      title: '微信绑定',
      key: 'wechat',
      width: 120,
      render: (_, record) => (
        record.wechat_openid ? (
          <Tag color="green" icon={<WechatOutlined />}>已绑定</Tag>
        ) : (
          <Tag color="default">未绑定</Tag>
        )
      )
    },
    {
      title: '联系方式',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (phone) => phone || '-'
    },
    {
      title: '所属学校',
      dataIndex: 'school_name',
      key: 'school_name',
      width: 150,
      render: (name) => name || '未分配'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : '禁用'}
        </Tag>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (time) => time ? new Date(time).toLocaleDateString() : '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedParent(record);
              fetchBoundStudents(record.id);
              setDetailModalVisible(true);
            }}
          >
            详情
          </Button>
          {authUser?.role === 'super_admin' && (
            <Popconfirm
              title="确定要删除此家长吗？"
              onConfirm={() => handleDeleteParent(record.id, record.name)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span><TeamOutlined /> 家长管理</span>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchParents(pagination.current, pagination.pageSize, searchKeyword)}
            >
              刷新
            </Button>
          </div>
        }
      >
        {/* 搜索 */}
        <div style={{ marginBottom: 16 }}>
          <Search
            placeholder="搜索家长姓名、手机号"
            style={{ width: 300 }}
            onSearch={setSearchKeyword}
            allowClear
          />
        </div>

        {/* 家长列表 */}
        <Table
          columns={columns}
          dataSource={parents}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 位家长`,
            onChange: (page, pageSize) => {
              fetchParents(page, pageSize, searchKeyword);
            }
          }}
        />
      </Card>

      {/* 家长详情弹窗 */}
      <Modal
        title="家长详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedParent(null);
          setBoundStudents([]);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {selectedParent && (
          <div>
            {/* 家长基本信息 */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar 
                src={selectedParent.wechat_avatar} 
                icon={<UserOutlined />}
                size={80}
              />
              <h3 style={{ marginTop: 12 }}>{selectedParent.name || selectedParent.wechat_nickname || '未设置'}</h3>
              <Tag color="orange">家长</Tag>
            </div>

            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="用户名">{selectedParent.username}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{selectedParent.phone || '未填写'}</Descriptions.Item>
              <Descriptions.Item label="所属学校" span={2}>
                {selectedParent.school_name || '未分配'}
              </Descriptions.Item>
              <Descriptions.Item label="微信昵称">
                {selectedParent.wechat_nickname || '未获取'}
              </Descriptions.Item>
              <Descriptions.Item label="账户状态">
                <Tag color={selectedParent.status === 'active' ? 'green' : 'red'}>
                  {selectedParent.status === 'active' ? '正常' : '禁用'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="注册时间" span={2}>
                {selectedParent.created_at ? new Date(selectedParent.created_at).toLocaleString() : '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* 绑定的学生 */}
            <div style={{ marginTop: 24 }}>
              <h4>绑定的学生</h4>
              {boundStudents.length > 0 ? (
                <Table
                  dataSource={boundStudents}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '学生姓名', dataIndex: 'student_name', key: 'student_name' },
                    { title: '班级', dataIndex: 'class_name', key: 'class_name' },
                    { title: '学校', dataIndex: 'school_name', key: 'school_name' },
                    { 
                      title: '绑定时间', 
                      dataIndex: 'created_at', 
                      key: 'created_at',
                      render: (time) => time ? new Date(time).toLocaleDateString() : '-'
                    }
                  ]}
                />
              ) : (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                  暂未绑定学生
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ParentList;






